import { Box, Flex, ThemeIcon, Tooltip } from '@mantine/core';
import { ReactNode } from 'react';
import { CommunicationTypeIcon } from '@/components/CommunicationTypeIcon';
import {
  isAppointmentGroup,
  isChatGroup,
  isEncounterGroup,
  isFaxGroup,
  isLabOrderGroup,
  isLabOrderResultsGroup,
  isPrescriptionOrderGroup,
  isSmsGroup,
  isStandaloneNoteGroup,
  isTaskCompletedGroup,
  isCiIntroCallGroup,
  isWarmHandoffGroup,
  isEligibilityFormGroup,
  isPaymentEventGroup,
  isBenefitEventGroup,
  isPaymentOrderGroup,
  isRefundEventGroup,
  isVoiceGroup,
  TimelineGroup,
} from '../types';
import {
  IconCalendarEvent,
  IconClipboardText,
  IconCheckbox,
  IconCreditCard,
  IconNote,
  IconPrescription,
  IconTestPipe,
  IconWorld,
  IconX,
} from '@tabler/icons-react';
import { Communication } from '@medplum/fhirtypes';
import { ACTIVITY_CATEGORY_COLORS, TIMELINE_MARKER_PROPS } from '../activityCategories';

interface TimelineItemWithIconProps {
  group: TimelineGroup;
  children: ReactNode;
  isLast?: boolean;
}

// Constants for timeline styling
// ICON_AREA_WIDTH = 28 wraps the 24px marker + its 2px Warm White ring snugly.
// Combined with gap="xs" (8px) on the outer Flex, this gives ~10px between
// the marker's right edge and the card's left edge — matches activity-
// timeline canon's "marker attached to the card on the spine" mental model
// without the full absolute-position refactor (deferred to Phase 7b).
const ICON_AREA_WIDTH = 28;
const LINE_WIDTH = 2;
const LINE_EXTENSION = 2000; // Extend line far down to connect with next items (clipped by container overflow)
const LINE_LEFT_OFFSET = ICON_AREA_WIDTH / 2 - LINE_WIDTH / 2; // Center line in icon area
const ICON_TOP_MARGIN = 4;
const LINE_COLOR = '#e9ecef';

// Canon: activity-timeline.mdx → 24px marker, 14px glyph, Warm White ring
// breaks the 2px gray spine line. Categorical color signals the activity
// group, not the individual type — see ../activityCategories.ts.
const actionIconCommonProps = TIMELINE_MARKER_PROPS;

function getTimelineIcon(group: TimelineGroup) {
  // Handle communication-based icons (voice, SMS, chat, fax)
  if (isVoiceGroup(group) || isSmsGroup(group) || isChatGroup(group) || isFaxGroup(group)) {
    const item = group.items[0];
    const communication = 'resource' in item ? (item.resource as Communication) : undefined;
    if (!communication) return null;

    return <CommunicationTypeIcon communication={communication} size={14} />;
  }

  // PAC intro call — Mustard (Notes category): the card is the chart note for the
  // call, not the call itself. The answers the rep records *are* the
  // documentation, and the call recording already has its own card, so this
  // files with the notes rather than the conversations.
  if (isCiIntroCallGroup(group)) {
    return (
      <Tooltip label="Intro call" position="top" withArrow>
        <ThemeIcon {...actionIconCommonProps} bg={ACTIVITY_CATEGORY_COLORS.notes} aria-label="Intro call" role="img">
          <IconClipboardText size={14} />
        </ThemeIcon>
      </Tooltip>
    );
  }

  // PAC warm hand-off — Mustard (Notes), for the same reason as the intro call.
  if (isWarmHandoffGroup(group)) {
    return (
      <Tooltip label="Warm hand-off" position="top" withArrow>
        <ThemeIcon {...actionIconCommonProps} bg={ACTIVITY_CATEGORY_COLORS.notes} aria-label="Warm hand-off" role="img">
          <IconClipboardText size={14} />
        </ThemeIcon>
      </Tooltip>
    );
  }

  // Task completed — Mustard, same category color as Notes
  if (isTaskCompletedGroup(group)) {
    return (
      <Tooltip label="Task completed" position="top" withArrow>
        <ThemeIcon
          {...actionIconCommonProps}
          bg={ACTIVITY_CATEGORY_COLORS.notes}
          aria-label="Task completed"
          role="img"
        >
          <IconCheckbox size={14} />
        </ThemeIcon>
      </Tooltip>
    );
  }

  // Notes group — Mustard (Notes category)
  if (isStandaloneNoteGroup(group)) {
    return (
      <Tooltip label="Note" position="top" withArrow>
        <ThemeIcon {...actionIconCommonProps} bg={ACTIVITY_CATEGORY_COLORS.notes} aria-label="Note" role="img">
          <IconNote size={14} />
        </ThemeIcon>
      </Tooltip>
    );
  }

  // Eligibility form — Mustard (Notes category), but a globe rather than a note
  // glyph: it keeps the card filed with the notes while signalling the entry
  // arrived from the public website rather than being typed in Orbit.
  if (isEligibilityFormGroup(group)) {
    return (
      <Tooltip label="Eligibility form" position="top" withArrow>
        <ThemeIcon
          {...actionIconCommonProps}
          bg={ACTIVITY_CATEGORY_COLORS.notes}
          aria-label="Eligibility form"
          role="img"
        >
          <IconWorld size={14} />
        </ThemeIcon>
      </Tooltip>
    );
  }

  // Lab orders / results — Dusty Rose (Orders & results category).
  // IconTestPipe (blood-draw tube) reads as "specimen sample"; IconMicroscope
  // skewed to "research lab" semantically when these are clinical labs.
  if (isLabOrderGroup(group) || isLabOrderResultsGroup(group)) {
    const label = isLabOrderGroup(group) ? 'Lab Order' : 'Lab Order Results';
    return (
      <Tooltip label={label} position="top" withArrow>
        <ThemeIcon {...actionIconCommonProps} bg={ACTIVITY_CATEGORY_COLORS.ordersResults} aria-label={label} role="img">
          <IconTestPipe size={14} />
        </ThemeIcon>
      </Tooltip>
    );
  }

  // Encounter (chart entry) — Mustard (clinical documentation = Notes category)
  if (isEncounterGroup(group)) {
    const encounter = group.items[0]?.encounter;
    const planDefinitionTitle = encounter?.tasks?.[0]?.basedOn?.[0]?.display;
    const label = planDefinitionTitle || 'Chart';
    return (
      <Tooltip label={label} position="top" withArrow>
        <ThemeIcon {...actionIconCommonProps} bg={ACTIVITY_CATEGORY_COLORS.notes} aria-label={label} role="img">
          <IconClipboardText size={14} />
        </ThemeIcon>
      </Tooltip>
    );
  }

  // Prescription — Dusty Rose (Orders & results — Rx is a clinical order).
  // Tabler IconPrescription matches the stroke-weight of every other marker
  // glyph; the previous custom RxIcon used a heavier 2px stroke against a
  // tighter "R" shape and read as visually bolder than its neighbors.
  if (isPrescriptionOrderGroup(group)) {
    return (
      <Tooltip label="Prescription Order" position="top" withArrow>
        <ThemeIcon
          {...actionIconCommonProps}
          bg={ACTIVITY_CATEGORY_COLORS.ordersResults}
          aria-label="Prescription Order"
          role="img"
        >
          <IconPrescription size={14} />
        </ThemeIcon>
      </Tooltip>
    );
  }

  // Billing — Teal, credit-card glyph. The order card and the money-moved cards
  // share one marker: they are the same activity group, and the card's own title
  // already says which of the two it is. Employer benefit events (BAR-2593) are billing too.
  // Refund requests (BAR-3052) too; the request and cancellation cards take a cross.
  if (
    isPaymentOrderGroup(group) ||
    isPaymentEventGroup(group) ||
    isBenefitEventGroup(group) ||
    isRefundEventGroup(group)
  ) {
    const cross = isRefundEventGroup(group) && ['requested', 'cancelled'].includes(group.items[0].kind);
    return (
      <Tooltip label="Billing" position="top" withArrow>
        <ThemeIcon {...actionIconCommonProps} bg={ACTIVITY_CATEGORY_COLORS.billing} aria-label="Billing" role="img">
          {cross ? <IconX size={14} /> : <IconCreditCard size={14} />}
        </ThemeIcon>
      </Tooltip>
    );
  }

  // Appointment — Slate Blue (Appointments category)
  if (isAppointmentGroup(group)) {
    return (
      <Tooltip label="Appointment" position="top" withArrow>
        <ThemeIcon
          {...actionIconCommonProps}
          bg={ACTIVITY_CATEGORY_COLORS.appointments}
          aria-label="Appointment"
          role="img"
        >
          <IconCalendarEvent size={14} />
        </ThemeIcon>
      </Tooltip>
    );
  }

  return null;
}

export function TimelineItemWithIcon({ group, children, isLast = false }: TimelineItemWithIconProps) {
  const icon = getTimelineIcon(group);

  return (
    <Flex gap="xs" align="flex-start" style={{ position: 'relative', width: '100%' }}>
      {/* Vertical timeline line - connects to the next item (not rendered for last item) */}
      {!isLast && (
        <Box
          style={{
            position: 'absolute',
            top: 0,
            bottom: -LINE_EXTENSION,
            left: LINE_LEFT_OFFSET,
            width: LINE_WIDTH,
            backgroundColor: LINE_COLOR,
            zIndex: 0,
          }}
        />
      )}

      {/* Icon container */}
      <Box
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
          width: ICON_AREA_WIDTH,
          zIndex: 1,
        }}
      >
        <Box style={{ marginTop: ICON_TOP_MARGIN }}>{icon}</Box>
      </Box>

      {/* Timeline item content */}
      <Box style={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 1 }}>{children}</Box>
    </Flex>
  );
}
